import { RoryQuote } from "./types";

// "Show me another" deals from a shuffled deck rather than picking at random,
// so nobody sees the same quote twice before they have seen them all. The
// Spectator column quotes are spread through the deck instead of clumping.
const DECK_KEY = "rq:deck";
const CURSOR_KEY = "rq:cursor";

const shuffle = (arr: number[]) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const buildDeck = (quotes: RoryQuote[]): number[] => {
  const spec = shuffle(quotes.filter((q) => q.spectator).map((q) => q.id));
  const rest = shuffle(quotes.filter((q) => !q.spectator).map((q) => q.id));
  const maxRun = Math.max(2, Math.ceil(spec.length / (rest.length + 1)));
  const order: number[] = [];
  let run = 0;
  while (spec.length || rest.length) {
    const canSpec = spec.length > 0 && run < maxRun;
    const takeSpec = canSpec && (rest.length === 0 || spec.length >= rest.length);
    if (takeSpec) {
      order.push(spec.pop() as number);
      run += 1;
    } else if (rest.length) {
      order.push(rest.pop() as number);
      run = 0;
    } else {
      order.push(spec.pop() as number);
      run += 1;
    }
  }
  return order;
};

export class Deck {
  private order: number[] | null = null;
  private cursor = 0;
  private seen = new Set<number>();

  constructor(private quotes: RoryQuote[], startingId?: number) {
    if (startingId) this.seen.add(startingId);
    this.restore();
  }

  private restore() {
    try {
      const stored = JSON.parse(localStorage.getItem(DECK_KEY) || "null");
      const cursor = Number(localStorage.getItem(CURSOR_KEY) || "0");
      const ids = new Set(this.quotes.map((q) => q.id));
      if (
        Array.isArray(stored) &&
        stored.length === this.quotes.length &&
        stored.every((id) => ids.has(id)) &&
        Number.isInteger(cursor) &&
        cursor >= 0 &&
        cursor <= stored.length
      ) {
        this.order = stored;
        this.cursor = cursor;
      }
    } catch {
      // Browsing still works when browser storage is unavailable.
    }
  }

  private persist() {
    try {
      localStorage.setItem(DECK_KEY, JSON.stringify(this.order));
      localStorage.setItem(CURSOR_KEY, String(this.cursor));
    } catch {
      // Keep the in-memory deck even if saving is blocked.
    }
  }

  // Nothing to deal from a collection of one or none; the caller hides the
  // button in that case.
  get canDeal(): boolean {
    return this.quotes.length > 1;
  }

  next(currentId?: number): RoryQuote {
    if (!this.canDeal) return this.quotes[0];
    for (;;) {
      if (!this.order || this.cursor >= this.order.length) {
        this.order = buildDeck(this.quotes);
        this.cursor = 0;
        if (this.seen.size >= this.quotes.length) this.seen = new Set(currentId ? [currentId] : []);
      }
      const id = this.order[this.cursor++];
      if (this.seen.has(id)) continue;
      this.seen.add(id);
      this.persist();
      const quote = this.quotes.find((q) => q.id === id);
      if (quote) return quote;
    }
  }

  markSeen(id: number) {
    this.seen.add(id);
  }
}
